import { useCallback, useEffect, useRef, useState } from 'react';

import { asApiError, type PSErrorClass } from '@/src/api/apiError';
import eventsApi, { type ShowcasePayload } from '@/src/api/eventsApi';

/**
 * useAccessPassShowcase — the `GET /accesspass-events-showcase` fetch behind the
 * Access Pass landing page.
 *
 * Same shape and the same reasoning as [[useHomePageEvents]] — read that one for
 * why `error` is the whole `ApiError`, why `retry` and `refresh` are the same
 * request wearing two different waits, and why nothing here toasts. One call
 * backs all four carousels, so they load, succeed and fail together.
 */

type FetchState =
  | { status: 'loading' }
  | { status: 'success'; data: ShowcasePayload }
  | { status: 'error'; error: PSErrorClass };

const LOADING: FetchState = { status: 'loading' };

export type AccessPassShowcaseState = {
  /** Non-null iff the last attempt succeeded. */
  data: ShowcasePayload | null;
  isLoading: boolean;
  /** Non-null iff the last attempt failed. Branch on `.kind`, show `.message`. */
  error: PSErrorClass | null;
  retry: () => void;
  refresh: () => Promise<void>;
};

export function useAccessPassShowcase(): AccessPassShowcaseState {
  const [state, setState] = useState<FetchState>(LOADING);
  // Bumped by `retry`; the effect keys off it, so a bump re-runs the fetch.
  const [attempt, setAttempt] = useState(0);
  const isMounted = useRef(true);

  const load = useCallback(async () => {
    try {
      const data = await eventsApi.getAccessPassShowcase();
      if (isMounted.current) setState({ status: 'success', data });
    } catch (error: unknown) {
      if (isMounted.current)
        setState({ status: 'error', error: asApiError(error) });
    }
  }, []);

  useEffect(() => {
    isMounted.current = true;
    // False positive: `load` only setStates after awaiting the request, so the
    // write lands in a later microtask, not synchronously in this body.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    load();

    return () => {
      isMounted.current = false;
    };
  }, [attempt, load]);

  const retry = useCallback(() => {
    setState(LOADING);
    setAttempt((n) => n + 1);
  }, []);

  return {
    data: state.status === 'success' ? state.data : null,
    isLoading: state.status === 'loading',
    error: state.status === 'error' ? state.error : null,
    retry,
    refresh: load,
  };
}
